import React, { useState } from "react"
import CustomButton from "components/Atoms/CustomButton"
import CustomDialog from "components/Atoms/CustomDialog"
import { Files, PlusCircle, SealCheck } from "@phosphor-icons/react"
import StackCardSummary from "./StackCardSummary"
import DynamicSelector from "components/Molecules/DynamicSelector"
import FormController from "components/Molecules/FormController"
import { ALL_HOLDING_AREAS } from "graphql/selector.queries.gql"
import { InwardAllocationType } from "generated/graphql"
import ButtonMutation from "components/Molecules/ButtonMutation"
import { CREATE_INWARD_ALLOCATION } from "graphql/production/allocation.mutation.gql"
import AppToaster from "utils/AppToaster"
import useLink from "hooks/useLink"
import Links from "constants/links"

export type StackCardDialogProps = {
  inwardAllocations: InwardAllocationType
}

const DEFAULT_FORM = {
  holdingArea: null,
}

export default function StackCardDialog({
  inwardAllocations,
}: StackCardDialogProps) {
  const [form, setForm] = useState(DEFAULT_FORM)
  const [isOpen, toggleOpen] = useState(false)
  const redirect = useLink()

  const isHoldingAreaAdded: boolean = inwardAllocations.holdingArea != null

  const handleChange = (path: string, value: any) => {
    setForm(prev => ({
      ...prev,
      [path]: value,
    }))
  }

  return (
    <>
      {isHoldingAreaAdded ? (
        <SealCheck
          size={24}
          weight="fill"
          color="#1C6E42"
          onClick={() => toggleOpen(true)}
          className="cursor-pointer"
        />
      ) : (
        <span
          className="flex items-center gap-1 cursor-pointer"
          onClick={() => toggleOpen(true)}
        >
          <PlusCircle size={24} />
          Card
        </span>
      )}

      <CustomDialog
        width="70%"
        light
        style={{ height: "800px" }}
        scrollable={false}
        fixedHeader
        isOpen={isOpen}
        onClose={() => toggleOpen(false)}
        footerShowCloseButton={false}
        footerRightButton={
          <div className="flex justify-end gap-3 pt-4 w-full">
            {isHoldingAreaAdded ? (
              <CustomButton
                text="Print Stack Card"
                icon="print"
                onClick={() =>
                  redirect(Links.STACK_CARD, { inward_allocation_id: inwardAllocations?.id })
                }
              />
            ) : (
              <ButtonMutation
                mutation={CREATE_INWARD_ALLOCATION}
                validate={() => {
                  if (!form.holdingArea?.id) {
                    AppToaster.show({
                      message: "Please select a Holding Area before saving",
                      intent: "warning",
                      icon: "warning-sign",
                    })
                    return false
                  }
                  return true
                }}
                variables={{
                  id: inwardAllocations?.id,
                  input: {
                    inwardId: inwardAllocations?.inward?.id,
                    holdingAreaId: form.holdingArea?.id,
                  },
                }}
                onError={() => {
                  AppToaster.show({
                    message: "Unable To Add Holding Area",
                    icon: "cross",
                    intent: "danger",
                  })
                }}
                onCompleted={() => {
                  AppToaster.show({
                    message: "Holding Area saved successfully!",
                    icon: "tick",
                    intent: "success",
                  })
                  toggleOpen(false)
                }}
              >
                Save
              </ButtonMutation>
            )}
          </div>
        }
        title={
          <div className="flex items-center gap-2">
            <Files size={24} /> Stack Card
          </div>
        }
      >
        <FormController source={form} onChange={handleChange}>
          
          <StackCardSummary inwardAllocations={inwardAllocations} isExpanded={true}  />

          {!isHoldingAreaAdded && (
            <div className="space-y-5">
              <div className="border-t pt-4 text-sm">
                <h6 className="font-600">Enter Holding area details</h6>
                <hr />
                <div className="w-60">
                  <DynamicSelector
                    btnClassName="vt-schedule-selector-button"
                    className="mb-0"
                    isServerSide
                    required
                    query={ALL_HOLDING_AREAS}
                    searchVector={["name"]}
                    name={["name"]}
                    path="holdingArea"
                    label="Holding Area"
                  />
                </div>
              </div>
            </div>
          )}
        </FormController>
      </CustomDialog>
    </>
  )
}
